import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { sanitizeBroadcastCareNeeds } from "@phi";

export interface CommunityBroadcast {
  id: string;
  broadcaster_user_id: string;
  lead_id: string | null;
  zip_code: string | null;
  care_needs: string | null;
  payer_source: string | null;
  hours_needed: number | null;
  urgency: number | null;
  notes: string | null;
  status: string;
  referred_to_user_id: string | null;
  client_notified_at: string | null;
  created_at: string;
  broadcaster_agency_name?: string;
}

export interface CommunityResponse {
  id: string;
  broadcast_id: string;
  responder_user_id: string;
  interested: boolean;
  created_at: string;
  responder_agency_name?: string;
  responder_phone?: string;
}

export function useCommunityBroadcasts() {
  const { user } = useAuth();
  const [broadcasts, setBroadcasts] = useState<CommunityBroadcast[]>([]);
  const [responses, setResponses] = useState<CommunityResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBroadcasts = useCallback(async () => {
    if (!user) { setBroadcasts([]); setLoading(false); return; }
    const { data } = await supabase
      .from("community_broadcasts")
      .select("*")
      .order("created_at", { ascending: false });

    const items = (data as any[]) ?? [];

    // Fetch broadcaster profiles
    const userIds = [...new Set(items.map(b => b.broadcaster_user_id))];
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, agency_name")
        .in("id", userIds);
      const profileMap = new Map((profiles ?? []).map((p: any) => [p.id, p.agency_name]));
      items.forEach(b => { b.broadcaster_agency_name = profileMap.get(b.broadcaster_user_id) || "Unknown Agency"; });
    }

    setBroadcasts(items);
    setLoading(false);
  }, [user]);

  const fetchResponses = useCallback(async () => {
    if (!user) { setResponses([]); return; }
    const { data } = await supabase
      .from("community_responses")
      .select("*");

    const items = (data as any[]) ?? [];

    // Fetch responder profiles
    const userIds = [...new Set(items.map(r => r.responder_user_id))];
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, agency_name, owner_phone")
        .in("id", userIds);
      const profileMap = new Map((profiles ?? []).map((p: any) => [p.id, { name: p.agency_name, phone: p.owner_phone }]));
      items.forEach(r => {
        const info = profileMap.get(r.responder_user_id);
        r.responder_agency_name = info?.name || "Unknown Agency";
        r.responder_phone = info?.phone || null;
      });
    }

    setResponses(items);
  }, [user]);

  useEffect(() => {
    fetchBroadcasts();
    fetchResponses();
  }, [fetchBroadcasts, fetchResponses]);

  // Poll instead of realtime — community_broadcasts contains PHI and was removed
  // from the realtime publication to prevent cross-tenant exposure.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      fetchBroadcasts();
      fetchResponses();
    }, 30000); // 30s polling
    return () => clearInterval(interval);
  }, [user, fetchBroadcasts, fetchResponses]);

  const createBroadcast = useCallback(async (lead: {
    id: string;
    zip_code?: string | null;
    acuity_notes?: string | null;
    payer_source?: string | null;
    hours_needed?: number | null;
    urgency?: number | null;
  }) => {
    if (!user) return;
    const { error } = await supabase.from("community_broadcasts").insert({
      broadcaster_user_id: user.id,
      lead_id: lead.id,
      zip_code: lead.zip_code || null,
      care_needs: sanitizeBroadcastCareNeeds(lead.acuity_notes),
      payer_source: lead.payer_source || null,
      hours_needed: lead.hours_needed || null,
      urgency: lead.urgency || null,
    } as any);
    if (error) throw error;
    await fetchBroadcasts();
  }, [user, fetchBroadcasts]);

  const closeBroadcast = useCallback(async (broadcastId: string) => {
    const { error } = await supabase
      .from("community_broadcasts")
      .update({ status: "closed" } as any)
      .eq("id", broadcastId);
    if (error) throw error;
    await fetchBroadcasts();
  }, [fetchBroadcasts]);

  const toggleInterest = useCallback(async (broadcastId: string, interested: boolean) => {
    if (!user) return;
    const existing = responses.find(r => r.broadcast_id === broadcastId && r.responder_user_id === user.id);
    if (existing) {
      const { error } = await supabase
        .from("community_responses")
        .update({ interested } as any)
        .eq("id", existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from("community_responses")
        .insert({ broadcast_id: broadcastId, responder_user_id: user.id, interested } as any);
      if (error) throw error;
    }
    await fetchResponses();
  }, [user, responses, fetchResponses]);

  const referToAgency = useCallback(async (broadcastId: string, receivingUserId: string) => {
    if (!user) return;
    // Call edge function to handle notification
    const { data, error } = await supabase.functions.invoke("notify-referral-transfer", {
      body: { broadcast_id: broadcastId, receiving_user_id: receivingUserId },
    });
    if (error) throw error;
    await fetchBroadcasts();
    return data;
  }, [user, fetchBroadcasts]);

  const getBroadcastForLead = useCallback((leadId: string) => {
    return broadcasts.find(b => b.lead_id === leadId && b.status !== "closed");
  }, [broadcasts]);

  const getResponsesForBroadcast = useCallback((broadcastId: string) => {
    return responses.filter(r => r.broadcast_id === broadcastId && r.interested);
  }, [responses]);

  const getMyResponse = useCallback((broadcastId: string) => {
    if (!user) return null;
    return responses.find(r => r.broadcast_id === broadcastId && r.responder_user_id === user.id) || null;
  }, [user, responses]);

  return {
    broadcasts,
    responses,
    loading,
    createBroadcast,
    closeBroadcast,
    toggleInterest,
    referToAgency,
    getBroadcastForLead,
    getResponsesForBroadcast,
    getMyResponse,
    refetch: fetchBroadcasts,
  };
}
